import { Injectable, signal } from '@angular/core';

import { StorageService } from './storage.service';
import { ThemePreference } from '../types/theme.type';

/**
 * Gestisce il tema aspetto dell'app (chiaro / scuro / sistema).
 *
 * Meccanismo:
 * - 'system' (default): nessuna classe sul root → il CSS segue
 *   `@media (prefers-color-scheme: dark)` (vedi src/theme/dark.scss).
 * - 'light' / 'dark': aggiunge la classe `theme-light` / `theme-dark` su
 *   `<html>`, che ha priorità sul media query e forza il tema.
 *
 * La preferenza è persistita localmente via StorageService.
 */
@Injectable({
	providedIn: 'root',
})
export class ThemeService {

	public preference$ = signal<ThemePreference>('system');

	constructor(
		private storage: StorageService,
	) {
	}

	/** Legge la preferenza salvata e la applica. Chiamato in fase di init dell'app. */
	public async init(): Promise<void> {
		const pref = (await this.storage.getThemePreference()) ?? 'system';
		this.preference$.set(pref);
		this.applyClass(pref);
	}

	/** Imposta, applica e persiste la preferenza di tema. */
	public async setPreference(pref: ThemePreference): Promise<void> {
		this.preference$.set(pref);
		this.applyClass(pref);
		await this.storage.setThemePreference(pref);
	}

	private applyClass(pref: ThemePreference): void {
		const el = document.documentElement;
		el.classList.toggle('theme-dark', pref === 'dark');
		el.classList.toggle('theme-light', pref === 'light');
	}
}
